import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Plus, User, LogOut, Settings, LayoutList, Shield, Cpu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useCompare } from "@/context/CompareContext";

const NAV = [
  { to: "/ilanlar", label: "İlanlar" },
  { to: "/katalog", label: "Katalog" },
  { to: "/drone-topla", label: "Drone Topla" },
  { to: "/karsilastir", label: "Karşılaştır" },
];

function NavLinks({ onClick, compareCount }) {
  return NAV.map((n) => (
    <NavLink key={n.to} to={n.to} onClick={onClick} data-testid={`nav-${n.to.slice(1)}`}
      className={({ isActive }) => `px-3 py-2 text-[13px] transition-colors ${isActive ? "text-zinc-900" : "text-zinc-500 hover:text-zinc-900"}`}>
      {n.label}
      {n.to === "/karsilastir" && compareCount > 0 && (
        <span data-testid="compare-count" className="ml-1 rounded bg-[#0071e3] px-1.5 text-[10px] font-mono text-white">{compareCount}</span>
      )}
    </NavLink>
  ));
}

function UserMenu() {
  const { user, signOut, isAdmin } = useAuth();
  const nav = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="user-menu-trigger" aria-label="Kullanıcı menüsü" className="flex items-center gap-2 rounded-full bg-black/[0.04] px-3 py-1.5 text-[13px] transition-colors hover:bg-black/[0.07] focus:outline-none focus:ring-2 focus:ring-[#0071e3]">
          <User className="h-4 w-4" /><span className="hidden sm:inline max-w-[120px] truncate">{user.full_name || user.email}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem data-testid="menu-my-listings" onClick={() => nav("/ilanlarim")}><LayoutList className="mr-2 h-4 w-4" />İlanlarım</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-my-builds" onClick={() => nav("/drone-topla")}><Cpu className="mr-2 h-4 w-4" />Toplamalarım</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-settings" onClick={() => nav("/ayarlar")}><Settings className="mr-2 h-4 w-4" />Hesap ayarları</DropdownMenuItem>
        {isAdmin && <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/yonetim")}><Shield className="mr-2 h-4 w-4" />Yönetim paneli</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={() => { signOut(); nav("/"); }}><LogOut className="mr-2 h-4 w-4" />Çıkış yap</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header() {
  const { user } = useAuth();
  const { items } = useCompare();
  return (
    <header className="sticky top-0 z-40 border-b border-black/[0.06] bg-white/75 backdrop-blur-2xl backdrop-saturate-150">
      <div className="mx-auto flex h-14 max-w-6xl items-center gap-4 px-4 sm:px-6">
        <Sheet>
          <SheetTrigger asChild><button className="md:hidden focus:outline-none focus:ring-2 focus:ring-[#0071e3] p-1 rounded" data-testid="mobile-menu-button" aria-label="Gezinme menüsünü aç"><Menu className="h-5 w-5" /></button></SheetTrigger>
          <SheetContent side="left" className="flex flex-col gap-1 bg-white pt-12"><SheetTitle className="sr-only">Menü</SheetTitle><NavLinks compareCount={items.length} /></SheetContent>
        </Sheet>
        <Link to="/" data-testid="logo-link" aria-label="CreateFPV Ana Sayfa" className="font-display text-[17px] font-semibold tracking-tight text-zinc-900 focus:outline-none focus:ring-2 focus:ring-[#0071e3] rounded">
          Create<span className="text-zinc-400">FPV</span>
        </Link>
        <nav className="hidden md:flex items-center mx-auto" aria-label="Ana gezinme menüsü"><NavLinks compareCount={items.length} /></nav>
        <div className="ml-auto md:ml-0 flex items-center gap-2">
          <Button asChild size="sm" className="h-8 bg-[#0071e3] hover:bg-[#0077ed] text-white" data-testid="header-create-listing">
            <Link to="/ilan-ver"><Plus className="h-4 w-4" /><span className="hidden sm:inline">İlan ver</span></Link>
          </Button>
          {user ? <UserMenu /> : (
            <Button asChild size="sm" variant="outline" className="h-8 border-0 bg-black/[0.04] hover:bg-black/[0.07]" data-testid="header-login">
              <Link to="/giris">Giriş yap</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
